'use client';
import { useState } from 'react';
import { ArticleComponent, ArticleProps } from '../../components/Article';
import BackToTop from '../../components/BackToTop';
import { CollapsibleSection } from '../../components/CollapsibleSection/CollapsibleSection';
import { RawHtml } from '../../components/RawHtml/RawHtml';
import { FaqBackgroundColor } from '../../types/cmsEnums';
import { PushDataLayerFn } from '../../types/pushDataLayer';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { resolveRelevantArticleCard } from '../../utils/relevantArticleLinks';
import { ComponentNestedContentElement } from './types';

interface FAQProps {
  titleReq: string;
  backgroundColor?: FaqBackgroundColor;
  nested_content_elements?: ComponentNestedContentElement[];
  pushDataLayer: PushDataLayerFn | undefined;
  onItemToggle?: (payload: { isOpen: boolean; index: number }) => void;
  backToTopVisible?: boolean;
}

const ItemFAQ: React.FC<
  ComponentNestedContentElement & {
    isOpen: boolean;
    onToggle: () => void;
    pushDataLayer: PushDataLayerFn | undefined;
  }
> = ({
  title,
  content,
  articles,
  material_icon,
  id,
  isOpen,
  onToggle,
  pushDataLayer,
}) => {
  const { brand, langPrefix } = useCmsClientContext();

  const relevantArticles: Omit<ArticleProps, 'pushDataLayer'>[] | undefined =
    articles
      ?.map((art) => resolveRelevantArticleCard(art, { brand, langPrefix }))
      .filter((x) => x !== undefined);

  return (
    <li>
      <CollapsibleSection
        id={id ?? ''}
        isOpen={isOpen}
        onToggle={onToggle}
        className="w-full"
        buttonClassName="flex items-center w-full justify-between"
        contentClassName=""
        iconClassName="text-font-size/bullet-1 overflow-visible"
        iconName={material_icon}
        content={
          <>
            {content && (
              <RawHtml
                className="mhg-alpha-typography-body-1 flex flex-col gap-spacing/gap/y-sm p-spacing/padding/soft-12px pt-spacing/padding/y text-colors/neutral-gray-800 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6"
                content={content}
              />
            )}
            {relevantArticles && (
              <div>
                {relevantArticles.map((article, index) => {
                  if (!article) {
                    return null;
                  }

                  const articleKey = [
                    'faq-article',
                    article.url ?? '',
                    article.title ?? '',
                    index,
                  ].join('_');

                  return (
                    <div key={articleKey} className="w-full">
                      <ArticleComponent
                        url={article.url}
                        title={article.title}
                        subtitle={article.subtitle}
                        pushDataLayer={pushDataLayer}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </>
        }
      >
        {title}
      </CollapsibleSection>
    </li>
  );
};

export const FAQ: React.FC<FAQProps> = ({
  titleReq,
  nested_content_elements,
  pushDataLayer,
  onItemToggle,
  backToTopVisible = true,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  // const getBackgroundClass = (
  //   color: FaqBackgroundColor | undefined,
  // ): string => {
  //   if (!color) return '';

  //   switch (color) {
  //     case 'GREY':
  //       return 'bg-fill/base';
  //     case 'WHITE':
  //       return 'bg-fill/card';
  //     case 'PRIMARY_LIGHT':
  //       return 'bg-fill/primary-light';
  //     default:
  //       return '';
  //   }
  // };

  const handleToggle = (id: string) => {
    setActiveId(activeId === id ? null : id);
  };

  return (
    <div className="">
      {/* ToDo: return background div with correct positioning */}
      {/* <div
        // eslint-disable-next-line tailwindcss/no-arbitrary-value
        className={classNames(
          'absolute',
          'inset-0',
          'left-[calc(-1*(100vw-100%+5px)/2)]',
          'right-[calc(-1*(100vw-100%+5px)/2)]',
          'w-[calc(100vw-5px)]',
          'z-0',
          backgroundClass,
        )}
      ></div> */}
      <div className="relative z-10 flex flex-col gap-spacing/gap/y pb-spacing/padding/section-y">
        <h2 className="mhg-alpha-typography-display-1 text-gray-900">
          {titleReq}
        </h2>
        <div className="overflow-hidden rounded-radius/card border border-borders/default p-spacing/gap/soft-12px bg-fill/card">
          <ul className="flex flex-col gap-spacing/gap/soft-12px">
            {nested_content_elements?.map((faq, index) => {
              const isOpen = activeId === faq.id;
              return (
                <ItemFAQ
                  key={faq.id ?? `faq-${index}`}
                  {...faq}
                  isOpen={isOpen}
                  onToggle={() => {
                    handleToggle(faq.id ?? '');
                    onItemToggle?.({ isOpen: !isOpen, index });
                  }}
                  pushDataLayer={pushDataLayer}
                />
              );
            })}
          </ul>
        </div>
        {backToTopVisible && <BackToTop />}
      </div>
    </div>
  );
};
